import React, { useContext, useEffect, useState } from 'react'

import {
  COLUMN_NAMES as DUEOUT_COLUMN_NAMES,
  DueOut,
  DueOutFetchArgs,
  getDueOutsViews,
} from '../../assets/ts/data/dueout/dueout'
import { STATUS_CODE } from '../../assets/ts/data/dueout/dueout-status-metadata'
import { twoDigits } from '../../assets/ts/formatting/formatting'
import PageOverlayBusy from '../../components/page-overlay/page-overlay-busy'
import { CustomerDashboardDueoutsTable } from '../../components/table/customer-dashboard-dueouts-table'
import Tabs from '../../components/tabs/tabs'
import useDueOutCount from '../../hooks/dueout/use-dueout-count'
import useDueOutDistinctValues from '../../hooks/dueout/use-dueout-distinct-values'
import useDueOutStatusCounts from '../../hooks/dueout/use-dueout-status-counts'
import useDueOuts from '../../hooks/dueout/use-dueouts'
import usePageAccumulator from '../../hooks/usePageAccumulator'
import { SelectionContext } from '../../store/selection-context-provider'
import CustomerDashboardDueOutfilters from './customer-dashboard-dueouts-filters'

type Props = {
  filter: DueOutFetchArgs
  onClearAll: () => void
  onUpdateFilter: (args: DueOutFetchArgs , order: number) => void
  tabSelected : number
}

const CustomerDashboardDueouts = ({
  filter,
  onClearAll,
  onUpdateFilter,
  tabSelected,
}: Props) => {
  const selection = useContext(SelectionContext)
  const [KPIFilter, setKPIFilter] = useState<DueOutFetchArgs>({})
  const [KPITabSelected, setKPITabSelected] = useState(0)
  const [pageNum, setPageNum] = useState(0)
  const [exportFetchIsBusy, setExportFetchIsBusy] = useState(false)
  const [now, ] = useState(new Date())

  const updateKpiFilter = (updatedArgs: DueOutFetchArgs) => {
    setPageNum(0)
    setKPIFilter((priorArgs) => {
      return {
        ...priorArgs,
        ...updatedArgs,
      }
    })
  }

  const dueOutPaging: DueOutFetchArgs = {
    pageNumber: pageNum,
  }

  const { dueOutStatusCounts, dueOutStatusCountsIsLoading } =
    useDueOutStatusCounts(filter)

  const { dueOutCountValue: overdueDueOutCount } = useDueOutCount({
    ...filter,
    dueDateLessThanDateIgnoreTime: now,
    statusCodesFilter: [STATUS_CODE.IN_PROGRESS, STATUS_CODE.NOT_STARTED],
  })

  const { dueouts, dueoutsAreLoading } = useDueOuts({
    ...filter,
    ...KPIFilter,
    ...dueOutPaging,
  })

  const {
    dueOutCountIsLoading: kpiFilteredDueOutCountIsLoading,
    dueOutCountValue: kpiFilteredDueOutCount,
  } = useDueOutCount({
    ...filter,
    ...KPIFilter,
  })

  const kpiFilteredDueOutCountString = kpiFilteredDueOutCount.toLocaleString()

  const countByStatus = (statusCodes?: string[]) => {
    const count = dueOutStatusCounts
      .filter((item) => !statusCodes || statusCodes.includes(item.StatusCode))
      .reduce((acc, item) => item.DueOutGUID_count + acc, 0)
    const countString = count.toLocaleString()

    return countString
  }

  const handleKpiTabSelection = (tabNumber) => {
    selection.reset()
    setKPITabSelected(tabNumber)
    switch (tabNumber) {
      case 0: // Total Due Outs
        updateKpiFilter({
          dueDateLessThanDateIgnoreTime: undefined,
          statusCodesFilter: [],
        })

        break
      case 1: // Not Started
        updateKpiFilter({
          dueDateLessThanDateIgnoreTime: undefined,
          statusCodesFilter: [STATUS_CODE.NOT_STARTED],
        })
        break
      case 2: // In Progress
        updateKpiFilter({
          dueDateLessThanDateIgnoreTime: undefined,
          statusCodesFilter: [STATUS_CODE.IN_PROGRESS],
        })
        break
      case 3: // Completed
        updateKpiFilter({
          dueDateLessThanDateIgnoreTime: undefined,
          statusCodesFilter: [STATUS_CODE.COMPLETED],
        })
        break
      case 4: // Overdue
        updateKpiFilter({
          dueDateLessThanDateIgnoreTime: now,
          statusCodesFilter: [STATUS_CODE.IN_PROGRESS, STATUS_CODE.NOT_STARTED],
        })
        break
    }
  }

  const accumulatedDueOuts = usePageAccumulator(pageNum, dueouts)

  useEffect(() => {
    selection.mergeWithAllLoadedRecordKeys(dueouts.map((i) => i.DueOutGUID ?? ''))
  }, [dueouts])
  const handleBottomScrollReached = () => {
    if (kpiFilteredDueOutCount > accumulatedDueOuts.length) {
      setPageNum((num) => num + 1)
    }
  }

  // When determining the allowed values for the dashboard filters,
  // consider only entries for the current user.  Also use the same value
  // of "isArchived" from the overall list filter
  const { dueOutDistinctValues } = useDueOutDistinctValues({
    assignedComponentCodeFilter: filter.assignedComponentCodeFilter,
    distinctFiltersToQuery: [
      DUEOUT_COLUMN_NAMES.FY,
      DUEOUT_COLUMN_NAMES.AssignedComponentCode,
      DUEOUT_COLUMN_NAMES.ServicingMAJCOMCode,
      DUEOUT_COLUMN_NAMES.ServicingBy,
      DUEOUT_COLUMN_NAMES.ServicingOrganization,
      DUEOUT_COLUMN_NAMES.SectionCode,
    ],
    isArchivedFilter: filter.isArchivedFilter,
    relatedUserFilter: filter.relatedUserFilter,
    sectionCodeFilter: filter.sectionCodeFilter,
    servicingByFilter: filter.servicingByFilter,
    servicingMAJCOMCodeFilter: filter.servicingMAJCOMCodeFilter,
    servicingOrganizationFilter: filter.servicingOrganizationFilter,
    yearFilter: filter.yearFilter,
  })

  const CustomerDueoutsitems = [
    {
      content: (
        <CustomerDashboardDueoutsTable
          count={twoDigits(kpiFilteredDueOutCountString)}
          countNumber = {kpiFilteredDueOutCount}
          dueOuts={accumulatedDueOuts}
          getAllSelectedRecords={getAllSelectedRecords}
          onBottomScrollReached={handleBottomScrollReached}
          title='Total Due Outs'
        />
      ),
      count: twoDigits(
        countByStatus([
          STATUS_CODE.CANCELED,
          STATUS_CODE.CLOSED,
          STATUS_CODE.COMPLETED,
          STATUS_CODE.IN_PROGRESS,
          STATUS_CODE.NOT_STARTED,
        ])
      ),
      isActive: KPITabSelected === 0,
      tabTitle: 'Total Due Outs',
    },

    {
      content: (
        <CustomerDashboardDueoutsTable
          count={twoDigits(kpiFilteredDueOutCountString)}
          countNumber = {kpiFilteredDueOutCount}
          dueOuts={accumulatedDueOuts}
          getAllSelectedRecords={getAllSelectedRecords}
          onBottomScrollReached={handleBottomScrollReached}
          title='Not Started'
        />
      ),
      count: twoDigits(countByStatus([STATUS_CODE.NOT_STARTED])),
      isActive: KPITabSelected === 1,
      tabTitle: 'Not Started',
    },
    {
      content: (
        <CustomerDashboardDueoutsTable
          count={twoDigits(kpiFilteredDueOutCountString)}
          countNumber = {kpiFilteredDueOutCount}
          dueOuts={accumulatedDueOuts}
          getAllSelectedRecords={getAllSelectedRecords}
          onBottomScrollReached={handleBottomScrollReached}
          title='In Progress'
        />
      ),
      count: twoDigits(countByStatus([STATUS_CODE.IN_PROGRESS])),
      isActive: KPITabSelected === 2,
      tabTitle: 'In Progress',
    },
    {
      content: (
        <CustomerDashboardDueoutsTable
          count={twoDigits(kpiFilteredDueOutCountString)}
          countNumber = {kpiFilteredDueOutCount}
          dueOuts={accumulatedDueOuts}
          getAllSelectedRecords={getAllSelectedRecords}
          onBottomScrollReached={handleBottomScrollReached}
          title='Completed'
        />
      ),
      count: twoDigits(countByStatus([STATUS_CODE.COMPLETED])),
      isActive: KPITabSelected === 3,
      tabTitle: 'Completed',
    },
    {
      content: (
        <CustomerDashboardDueoutsTable
          count={twoDigits(kpiFilteredDueOutCountString)}
          countNumber = {kpiFilteredDueOutCount}
          dueOuts={accumulatedDueOuts}
          getAllSelectedRecords={getAllSelectedRecords}
          onBottomScrollReached={handleBottomScrollReached}
          title='OverDue'
        />
      ),
      count: twoDigits(overdueDueOutCount),
      isActive: KPITabSelected === 4,
      tabTitle: 'OverDue',
    },
  ]

  const handleDashboardFilterChange = (e , order) => {
    selection.reset()
    if (onUpdateFilter) {
      setPageNum(0)
      onUpdateFilter(e , order)
    }
  }

  const busy =
    dueoutsAreLoading ||
    kpiFilteredDueOutCountIsLoading ||
    dueOutStatusCountsIsLoading ||
    exportFetchIsBusy

  async function getAllSelectedRecords(): Promise<DueOut[]> {
    const visibleSelectedRecords = accumulatedDueOuts.filter((d) =>
      selection.keyIsSelected(d.DueOutGUID ?? '')
    )

    const unloadedSelectedRecords = await getAllUnloadedSelectedRecords()

    const allSelectedRecords = [
      ...visibleSelectedRecords,
      ...unloadedSelectedRecords,
    ]

    return allSelectedRecords
  }

  async function getAllUnloadedSelectedRecords(): Promise<DueOut[]> {
    if (selection.unloadedRecordsAreSelected) {
      setExportFetchIsBusy(true)
      // Becuase the user clicked the Select All in the past, all records
      // matching the current dashboard and KPI filters that are in pages
      // past the currently loaded/visible ones need to be fetched
      const items = await getDueOutsViews({
        ...filter,
        ...KPIFilter,
        onePageOnly: false,
        pageNumber: (dueOutPaging.pageNumber ?? 0) + 1,
      })
      setExportFetchIsBusy(false)
      return items
    } else {
      return []
    }
  }
  useEffect(()=>{
    setKPIFilter({})
    setKPITabSelected(0)
    setPageNum(0)
    selection.reset()
    onClearAll()
  },[tabSelected])

  return (
    <>
      {busy && <PageOverlayBusy />}
      <div className='d-flex flex-column card-body h-100'>
        <div className='d-flex justify-content-between align-items-start align-items-sm-center flex-wrap'>
          <CustomerDashboardDueOutfilters
            availableFilterValues={dueOutDistinctValues[0]}
            filter={filter}
            onClearAll={() => {
              setKPIFilter({})
              setKPITabSelected(0)
              setPageNum(0)
              selection.reset()
              onClearAll()
            }}
            onUpdateFilter={handleDashboardFilterChange}
            tabSelected={tabSelected}
          />
        </div>

        <Tabs
          anchorclass='card d-flex kpiborder justify-content-between card-body h-100 dashboard-kpi-title ps-3 d-flex align-items-center poppins-bold font-12 title-color13 border1'
          badge='border-left1 p-2 px-3 color-primary d-flex align-items-center poppins-bold font-22'
          className='latobold darktext tabKpis'
          items={CustomerDueoutsitems}
          onTabSelected={handleKpiTabSelection}
          tablistclassName='font-13 col'
        />
      </div>
      <div></div>
    </>
  )
}
export default CustomerDashboardDueouts
